
import { useTranslation } from 'react-i18next';
import { CrudPage } from '../../components/CrudPage';
import type { GridColumnMeta } from '../../components/CrudPage';
import type { CampaignSequenceDto } from '../../api/models/CampaignSequenceDto';
import { CampaignSequenceFormDialog } from '../forms/CampaignSequenceFormDialog';
import { CampaignSequenceControllerService } from '../../api/services/CampaignSequenceControllerService';

type CampaignSequenceWithId = CampaignSequenceDto & { id: string };

const CampaignSequencesPage = () => {
  const { t } = useTranslation();

  const formatWeekday = (data: any) => {
    // Syncfusion Grid passes data in a wrapper object
    const sequence = data?.data || data?.rowData || data;
    if (!sequence || !sequence.dayOfWeek) return '';
    return t(`weekday.${sequence.dayOfWeek}`);
  };

  const formatEntryCount = (data: any) => {
    // Syncfusion Grid passes data in a wrapper object
    const sequence = data?.data || data?.rowData || data;
    if (!sequence || !sequence.entries) return '0';
    return String(sequence.entries.length);
  };

  const formatDefault = (data: any) => {
    const sequence = data?.data || data?.rowData || data;
    return sequence?.isDefault ? '✓' : '';
  };

  const gridColumns: GridColumnMeta[] = [
    { 
      field: 'name', 
      headerKey: 'campaignSequence.grid.field.name', 
      width: 200 
    },
    { 
      field: 'dayOfWeek', 
      headerKey: 'campaignSequence.grid.field.weekday', 
      width: 120,
      render: formatWeekday
    },
    { 
      field: 'isDefault', 
      headerKey: 'campaignSequence.grid.field.isDefault', 
      width: 80,
      align: 'Center',
      render: formatDefault
    },
    { 
      field: 'description', 
      headerKey: 'campaignSequence.grid.field.description', 
      width: 280 
    },
    { 
      field: 'entries', 
      headerKey: 'campaignSequence.grid.field.entryCount', 
      width: 100,
      align: 'Center',
      render: formatEntryCount
    }
  ];

  return (
    <CrudPage<CampaignSequenceWithId>
      titleKey="campaignSequence.title"
      gridMeta={gridColumns}
      fetchData={async () => {
        const sequences = await CampaignSequenceControllerService.getAllCampaignSequences();
        return sequences.map(sequence => ({
          ...sequence,
          id: sequence.uuid || ''
        }));
      }}
      saveData={async (data: CampaignSequenceWithId) => {
          // Convert to API format (remove id field, use uuid)
          const { id, ...apiData } = data;

          if (data.id && data.uuid) {
            await CampaignSequenceControllerService.updateCampaignSequence(data.uuid, apiData);
          } else {
            await CampaignSequenceControllerService.createCampaignSequence(apiData);
          }
      }}
      deleteData={async (id: string) => {
        try {
          await CampaignSequenceControllerService.deleteCampaignSequence(id);
        } catch (error: any) {
          console.error('CampaignSequencesPage: Error deleting campaign sequence:', error);
          throw error;
        }
      }}
      FormComponent={CampaignSequenceFormDialog}
      entityName="campaignSequence"
    />
  );
};

export default CampaignSequencesPage;
